import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { modeConstants } from "../../../../constants";
import { useDispatch, useSelector } from "react-redux";
import { companyActions } from "../../../../redux/actions";
import PhoneInput from "react-phone-input-2";
import { industryOptions } from "../../../../shared/utility";
import JoditEditorComponent from "../../../../shared/SummernoteEditor";

const schema = z.object({
  companyName: z
    .string({
      required_error: "Company name is required",
      invalid_type_error: "Company name must be a string",
    })
    .min(1, "The field is required")
    .max(100, "Maximum 100 characters allowed"),

  website: z
    .string({
      required_error: "The field is required",
    })
    .url("Enter a valid URL"),

  country: z
    .string({
      required_error: "The field is required",
    })
    .min(2, "The field is required")
    .max(100, "Maximum 100 characters allowed"),

  state: z
    .string({
      required_error: "The field is required",
    })
    .min(2, "The field is required")
    .max(100, "Maximum 100 characters allowed"),

  city: z
    .string({
      required_error: "The field is required",
    })
    .min(2, "The field is required")
    .max(100, "Maximum 100 characters allowed"),

  industry: z
    .string({
      required_error: "Industry is required",
    }),

  contactNumber: z
    .string({
      required_error: "Contact number is required",
    })
    .regex(/^\d{11,}$/, "Contact number must be at least 11 digits")
    .max(15, "Maximum 15 digits allowed"),

  location: z
    .string({
      required_error: "Location is required",
    }),

  detail: z
    .string()
    .optional(),

  logo: z.any().optional(),
});


const EMPTY_VALUES = {
  companyName: "",
  website: "",
  country: "",
  state: "",
  city: "",
  contactNumber: "",
  location: "",
  industry: "",
  detail: "",
  logo: null,
};

export default function CompanyModal({ showModal, setShowModal, mode }) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: EMPTY_VALUES,
    mode: "onChange",          // validate on typing
    reValidateMode: "onChange",
    criteriaMode: "all",
  });

  const dispatch = useDispatch();
  const isAdd = mode === modeConstants.ADD_MODE;
  const isEdit = mode === modeConstants.UPDATE_MODE;
  const isView = mode === modeConstants.VIEW_MODE;
  const [countryCode, setCountryCode] = useState("pk");
  const initialData = useSelector((s) => s.companyReducer.formData);

  // Optional logo preview
  const [logoPreview, setLogoPreview] = useState(null);
  const logoFile = watch("logo");
  useEffect(() => {
    if (logoFile instanceof File) {
      const url = URL.createObjectURL(logoFile);
      setLogoPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setLogoPreview(null);
  }, [logoFile]);

  // 🔁 Prefill from initialData when editing/viewing; clear when adding
  useEffect(() => {
    if (isAdd) {
      reset(EMPTY_VALUES);
      setLogoPreview(null);
      return;
    }
    if (initialData) {
      reset({
        companyName: initialData.companyName ?? "",
        website: initialData.websiteLink ?? "",
        country: initialData.country ?? "",
        state: initialData.state ?? "",
        city: initialData.city ?? "",
        contactNumber: String(initialData.contactNumber) || "",
        location: initialData.location ?? "",
        industry: initialData?.industry ?? "",
        detail: initialData.details ?? "",
        logo: null,
      });
      setLogoPreview(process.env.REACT_APP_STORAGE_URL + initialData.logoUrl);
    }
  }, [isAdd, initialData, reset]);

  const handleHide = () => {
    reset(EMPTY_VALUES);
    setLogoPreview(null);
    setShowModal(false);
  };

  const onSubmit = async (data) => {
    // --- Normalize contact number: remove non-digits and prefix 92 ---
    const digitsOnly = (data.contactNumber || "").replace(/\D+/g, "");
    const contactNumber = digitsOnly;

    // --- Build FormData ---
    const fd = new FormData();
    // append simple fields
    fd.append("companyName", data.companyName?.trim() ?? "");
    fd.append("websiteLink", data.website?.trim() ?? "");
    fd.append("country", data.country?.trim() ?? "");
    fd.append("state", data.state?.trim() ?? "");
    fd.append("city", data.city?.trim() ?? "");
    fd.append("contactNumber", contactNumber);
    fd.append("location", data.location?.trim() ?? "");
    fd.append("industry", data.industry || "");
    fd.append("details", data.detail?.trim() ?? "");

    // logo: may be File or null
    const logoFile = data.logo instanceof File ? data.logo : null;
    if (logoFile) {
      // append file with a field name of "logo"
      fd.append("logo", logoFile, logoFile.name);
    } else if (initialData?.logoUrl && isEdit) {
      // Choose one convention your backend expects; here I send a flag plus URL.
      fd.append("keepExistingLogo", "true");
      fd.append("logoUrl", initialData.logoUrl);
    }

    // If editing, include the record id so backend can update
    if (isEdit && initialData?.id) {
      fd.append("id", String(initialData.id));
    }
    try {
      if (isEdit) {
        dispatch(companyActions.updateCompany(fd));
      } else {
        dispatch(companyActions.createCompany(fd));
      }
      handleHide();
    } catch (err) {
      console.error("Network error while uploading company:", err);
      // TODO: show UI error / toast
    }
  };

  const title = isAdd
    ? "Create Company"
    : isEdit
      ? "Edit Company"
      : "View Company";

  return (
    <Modal show={showModal} onHide={handleHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="form-floating">
          <fieldset disabled={isView}>
            <div className="row">
              <div className="col-lg-9">
                <div className="row g-3">
                  <div className="col-12 col-lg-6">
                    <div class="form-floating">
                      <input
                        id="companyName"
                        className="form-control"
                        placeholder="Company Name"
                        {...register("companyName")}
                      />
                      <label for="companyName">Company Name</label>
                    </div>
                    {errors.companyName && (
                      <small className="text-danger">
                        {errors.companyName.message}
                      </small>
                    )}
                  </div>
                  <div className="col-12 col-lg-6">
                    <div class="form-floating">
                      <input
                        id="website"
                        className="form-control"
                        placeholder="Website Link"
                        type="url"
                        {...register("website")}
                      />
                      <label for="website">Website Link</label>
                    </div>
                    {errors.website && (
                      <small className="text-danger">
                        {errors.website.message}
                      </small>
                    )}
                  </div>

                  <div className="col-12 col-lg-6">
                    <div class="form-floating">
                      <input
                        id="country"
                        className="form-control"
                        placeholder="Country"
                        {...register("country")}
                      />
                      <label for="country">Country</label>
                    </div>
                    {errors.country && (
                      <small className="text-danger">
                        {errors.country.message}
                      </small>
                    )}
                  </div>
                  <div className="col-12 col-lg-6">
                    <div class="form-floating">
                      <input
                        id="state"
                        className="form-control"
                        placeholder="State"
                        {...register("state")}
                      />
                      <label for="state">State</label>
                    </div>
                    {errors.state && (
                      <small className="text-danger">
                        {errors.state.message}
                      </small>
                    )}
                  </div>

                  <div className="col-12 col-lg-6">
                    <div class="form-floating">
                      <input
                        id="city"
                        className="form-control"
                        placeholder="City"
                        {...register("city")}
                      />
                      <label for="city">City</label>
                    </div>
                    {errors.city && (
                      <small className="text-danger">
                        {errors.city.message}
                      </small>
                    )}
                  </div>
                  <div className="col-12 col-lg-6">
                    <div class="form-floating">
                      <Controller
                        name="contactNumber"
                        control={control}
                        rules={{
                          required: "The field is required",
                          validate: (val) =>
                            (val && val.length >= 8) || "Invalid number",
                        }}
                        render={({ field: { value, onChange } }) => (
                          <PhoneInput
                            country={countryCode}
                            value={value.toString()}
                            onChange={(val, country) => {
                              onChange(val);
                              if (country?.countryCode)
                                setCountryCode(country.countryCode);
                            }}
                            inputProps={{
                              name: "contactNumber",
                              required: true,
                              autoFocus: false,
                            }}
                            placeholder="Enter phone number"
                            enableSearch
                            disableSearchIcon={false}
                            inputStyle={{ width: "100%" }}
                            buttonStyle={{
                              borderTopLeftRadius: 6,
                              borderBottomLeftRadius: 6,
                            }}
                            containerStyle={{ width: "100%" }}
                            disabled={isView}
                          />
                        )}
                      />
                    </div>
                    {errors.contactNumber && (
                      <small className="text-danger">
                        {errors.contactNumber.message}
                      </small>
                    )}
                  </div>
                  <div className="col-12 col-lg-6">
                    <div class="form-floating">
                      <select
                        id="industry"
                        className="form-control"
                        {...register("industry", {
                          required: "The field is required",
                        })}
                      >
                        <option value="">Select Industry</option>
                        {industryOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                      <label for="industry">Industry</label>
                    </div>
                    {errors.industry && (
                      <small className="text-danger">
                        {errors.industry.message}
                      </small>
                    )}
                  </div>
                  <div className="col-12 col-lg-6">
                    <div class="form-floating">
                      <input
                        id="date"
                        className="form-control"
                        placeholder="Date"
                        value={
                          initialData?.createdAt && !isAdd
                            ? new Date(
                              initialData?.createdAt * 1000
                            ).toLocaleDateString()
                            : new Date().toLocaleDateString()
                        }
                        disabled
                      />
                      <label for="date">Date</label>
                    </div>
                  </div>
                  <div className="col-12 col-lg-12">
                    <div class="form-floating">
                      <input
                        id="location"
                        className="form-control"
                        placeholder="Location"
                        {...register("location")}
                      />
                      <label for="location">Location</label>
                    </div>
                    {errors.location && (
                      <small className="text-danger">
                        {errors.location.message}
                      </small>
                    )}
                  </div>

                  <div className="col-12">
                    <div class="form-floating">
                      {/* <textarea
                        id="detail"
                        className="form-control"
                        rows={9}
                        placeholder="Detail"
                        {...register("detail")}
                      /> */}
                      <JoditEditorComponent
                        defaultValue={watch("detail")}
                        onChange={(content) => setValue("detail", content)}
                        height={250}
                      />
                      {/* <label for="detail">Details</label> */}
                    </div>
                    {errors.detail && (
                      <small className="text-danger">
                        {errors.detail.message}
                      </small>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-3 d-flex flex-column align-items-center">
                <div
                  className="rounded-circle bg-primary-subtle d-flex align-items-center justify-content-center"
                  style={{ width: 120, height: 120, overflow: "hidden" }}
                >
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        borderRadius: "50%",
                      }}
                    />
                  ) : (
                    <i className="bi bi-person-circle fs-1 text-primary" />
                  )}
                </div>
                <label className="mt-3">Upload Logo</label>
                <input
                  type="file"
                  className="form-control mt-2"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null;

                    if (file) {
                      if (file.size > 2 * 1024 * 1024) {
                        alert("File size must be 2MB or less");
                        return;
                      }
                    }

                    setValue("logo", file, { shouldValidate: true });
                  }}
                />
              </div>
            </div>
          </fieldset>

          <div className="d-flex justify-content-end gap-2 footer-modal">
            <Button variant="outline-secondary btn-footer" onClick={handleHide}>
              {isView ? "Close" : "Cancel"}
            </Button>
            {!isView && (
              <Button type="submit" variant="success" className="btn-footer">
                {isEdit ? "Save Changes" : "Create"}
              </Button>
            )}
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
